import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles, Save, Loader2, Trash2, TrendingUp, AlertTriangle, CheckCircle2, Flame, Droplet, Trophy } from "lucide-react";
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, ReferenceArea, ResponsiveContainer, Tooltip } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import { toast } from "sonner";
import { celebrateAchievements } from "@/lib/achievements";

const METHODS = [
  { id: "v60", name_ar: "V60", name_en: "V60" },
  { id: "chemex", name_ar: "كيمكس", name_en: "Chemex" },
  { id: "espresso", name_ar: "إسبريسو", name_en: "Espresso" },
  { id: "aeropress", name_ar: "إيروبريس", name_en: "AeroPress" },
  { id: "frenchpress", name_ar: "فرنش بريس", name_en: "French Press" },
  { id: "moka", name_ar: "موكا بوت", name_en: "Moka Pot" },
];

const LABELS_AR = {
  under: "تحت الاستخلاص",
  ideal: "مثالي",
  over: "فوق الاستخلاص",
  weak: "خفيف",
  strong: "قوي",
};
const LABELS_EN = {
  under: "Under-extracted",
  ideal: "Ideal",
  over: "Over-extracted",
  weak: "Weak",
  strong: "Strong",
};

const labelColor = (label) => {
  if (label === "ideal") return "text-emerald-700 bg-emerald-50 border-emerald-200";
  if (label === "under" || label === "weak") return "text-amber-700 bg-amber-50 border-amber-200";
  return "text-rose-700 bg-rose-50 border-rose-200";
};

const METHOD_DEFAULTS = {
  v60: { dose: 20, yield: 300 },
  chemex: { dose: 30, yield: 500 },
  espresso: { dose: 18, yield: 36 },
  aeropress: { dose: 15, yield: 220 },
  frenchpress: { dose: 30, yield: 500 },
  moka: { dose: 18, yield: 90 },
};

export default function ExtractionCalculator() {
  const { lang, isRTL } = useI18n();
  const { user } = useAuth();
  const qc = useQueryClient();
  const serifClass = isRTL ? "font-serif-ar" : "font-serif-en";

  const [mode, setMode] = useState("pro"); // 'pro' | 'quick'
  const [method, setMethod] = useState("v60");
  const [dose, setDose] = useState(METHOD_DEFAULTS.v60.dose);
  const [yieldG, setYieldG] = useState(METHOD_DEFAULTS.v60.yield);
  const [tds, setTds] = useState(1.30);
  const [timeS, setTimeS] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [advice, setAdvice] = useState(null);
  const [adviceBusy, setAdviceBusy] = useState(false);
  const [notes, setNotes] = useState("");

  const isEspresso = method === "espresso" || method === "moka";

  // Active challenge for "Submit to challenge" button
  const { data: activeChallengeData } = useQuery({
    queryKey: ["active-challenge"],
    queryFn: endpoints.getActiveChallenge,
    staleTime: 60_000,
  });
  const activeChallenge = activeChallengeData?.challenge || null;
  const eligibleForChallenge = useMemo(() => {
    if (!activeChallenge || !result) return false;
    if ((result.method || "").toLowerCase() !== (activeChallenge.method || "").toLowerCase()) return false;
    const ey = Number(result.ey_pct || 0);
    return ey >= activeChallenge.ey_min && ey <= activeChallenge.ey_max;
  }, [activeChallenge, result]);
  const [submitted, setSubmitted] = useState(null);

  const onSubmitChallenge = async () => {
    if (!user) {
      toast.error(isRTL ? "سجّل الدخول لإرسال محاولتك" : "Sign in to submit your attempt");
      return;
    }
    try {
      const sub = await endpoints.submitChallenge({
        id: `manual_${Date.now()}`,
        method: result.method,
        dose_g: result.dose_g,
        yield_g: result.yield_g,
        tds_pct: result.tds_pct,
        ey_pct: result.ey_pct,
      });
      setSubmitted(sub.score);
      toast.success((isRTL ? "تم تسجيل محاولتك! +" : "Submission counted! +") + sub.score + (isRTL ? " نقطة" : " pts"));
      celebrateAchievements(sub.new_achievements, lang);
      qc.invalidateQueries({ queryKey: ["challenge-leaderboard"] });
      qc.invalidateQueries({ queryKey: ["my-achievements"] });
    } catch (e) {
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (code === 409 || detail === "already_submitted_today") {
        toast.error(
          isRTL
            ? "سجّلت محاولتك اليوم — جرّب مرة أخرى بكرا!"
            : "You already submitted today — try again tomorrow!"
        );
        setSubmitted(0); // mark as locked so the button hides
        return;
      }
      toast.error(detail || (isRTL ? "تعذّر الإرسال" : "Submit failed"));
    }
  };

  // When method changes, swap dose/yield/TDS defaults so user can still override.
  const onMethodChange = (next) => {
    setMethod(next);
    const d = METHOD_DEFAULTS[next] || METHOD_DEFAULTS.v60;
    setDose(d.dose);
    setYieldG(d.yield);
    setTds(next === "espresso" || next === "moka" ? 9.0 : 1.30);
  };

  const onCalc = async () => {
    setBusy(true);
    setAdvice(null);
    setSubmitted(null);
    try {
      const payload = {
        mode,
        method,
        dose_g: Number(dose),
        yield_g: Number(yieldG),
        tds_pct: mode === "pro" ? Number(tds) : undefined,
        time_s: timeS ? Number(timeS) : undefined,
      };
      const data = await endpoints.extractionCalculate(payload);
      setResult(data);
      // Auto-submission on first eligible calculation of the day (anti-cheat).
      if (data?.challenge_submission?.auto) {
        setSubmitted(data.challenge_submission.score);
        toast.success(
          (isRTL
            ? "تم تسجيلك تلقائياً في تحدّي الأسبوع! +"
            : "Auto-submitted to weekly challenge! +") +
            data.challenge_submission.score +
            (isRTL ? " نقطة" : " pts")
        );
        qc.invalidateQueries({ queryKey: ["challenge-leaderboard"] });
      } else if (data?.challenge_locked_today) {
        // Already submitted today — lock the manual submit button and show pill.
        setSubmitted(data.challenge_today_score || 0);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isRTL ? "تعذّر الحساب" : "Calculation failed"));
    } finally {
      setBusy(false);
    }
  };

  const onGetAdvice = async () => {
    if (!result) return;
    setAdviceBusy(true);
    try {
      const data = await endpoints.extractionAiAdvice(result);
      setAdvice(data);
    } catch {
      toast.error(isRTL ? "تعذّر جلب التوصية" : "Could not fetch advice");
    } finally {
      setAdviceBusy(false);
    }
  };

  const onSave = async () => {
    if (!result) return;
    if (!user) {
      toast.error(isRTL ? "سجّل الدخول لحفظ السجل" : "Sign in to save your brew log");
      return;
    }
    try {
      const res = await endpoints.brewLogCreate({
        method: result.method,
        dose_g: result.dose_g,
        yield_g: result.yield_g,
        tds_pct: result.tds_pct,
        time_s: timeS ? Number(timeS) : undefined,
        ey_pct: result.ey_pct,
        strength_label: result.strength_label,
        extraction_label: result.extraction_label,
        notes: notes.trim(),
      });
      toast.success(isRTL ? "تم الحفظ في السجل" : "Saved to brew log");
      if (res?.challenge_submission) {
        toast.success(`🏆 +${res.challenge_submission.score} ${isRTL ? "نقطة في تحدّي الأسبوع" : "pts on the weekly challenge"}`);
      }
      celebrateAchievements(res?.new_achievements, lang);
      setNotes("");
      qc.invalidateQueries({ queryKey: ["brew-log"] });
      qc.invalidateQueries({ queryKey: ["challenge-leaderboard"] });
      qc.invalidateQueries({ queryKey: ["my-achievements"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isRTL ? "تعذّر الحفظ" : "Save failed"));
    }
  };

  const idealEy = result?.ideal_ey || [18, 22];
  const idealTds = result?.ideal_tds || (isEspresso ? [8, 12] : [1.15, 1.45]);
  // Chart axes
  const xDomain = isEspresso ? [14, 26] : [14, 26];
  const yDomain = isEspresso ? [4, 14] : [0.8, 1.8];
  const dataPoint = result ? [{ x: result.ey_pct, y: result.tds_pct, label: lang === "ar" ? "نتيجتك" : "Your brew" }] : [];

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="extraction-calculator">
      {/* Inputs */}
      <div className="lg:col-span-5 soft-card rounded-2xl p-8">
        <div className="space-y-5">
          <div>
            <div className="inline-flex rounded-xl border border-[#2A2421]/15 overflow-hidden mb-4" role="tablist">
              <button
                type="button"
                onClick={() => setMode("pro")}
                className={`px-4 py-2 text-sm transition-colors ${mode === "pro" ? "bg-[#C86240] text-white" : "bg-white text-[#2A2421] hover:bg-[#F9F6F0]"}`}
                data-testid="ext-mode-pro"
              >
                {isRTL ? "احترافي (TDS)" : "Pro (TDS)"}
              </button>
              <button
                type="button"
                onClick={() => setMode("quick")}
                className={`px-4 py-2 text-sm transition-colors ${mode === "quick" ? "bg-[#C86240] text-white" : "bg-white text-[#2A2421] hover:bg-[#F9F6F0]"}`}
                data-testid="ext-mode-quick"
              >
                {isRTL ? "سريع" : "Quick"}
              </button>
            </div>
            <p className="text-xs text-[#5C544F]">
              {mode === "pro"
                ? (isRTL ? "للي عنده ريفراكتومتر — أدق نتيجة." : "If you own a refractometer — most accurate.")
                : (isRTL ? "بدون TDS — تقدير بناءً على النسبة والزمن." : "No TDS — estimated from ratio + time.")}
            </p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#5C544F]">{isRTL ? "طريقة التحضير" : "Brew method"}</Label>
            <Select value={method} onValueChange={onMethodChange}>
              <SelectTrigger className="mt-2 h-11 rounded-xl border-[#2A2421]/15" data-testid="ext-method-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {METHODS.map((m) => (
                  <SelectItem key={m.id} value={m.id} data-testid={`ext-method-${m.id}`}>
                    {lang === "ar" ? m.name_ar : m.name_en}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#5C544F]">
                {isRTL ? "الجرعة (g)" : "Dose (g)"}
              </Label>
              <Input type="number" step="0.1" min="1" value={dose} onChange={(e) => setDose(e.target.value)} className="mt-2 h-11 rounded-xl border-[#2A2421]/15" data-testid="ext-dose-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#5C544F]">
                {isEspresso ? (isRTL ? "وزن الكوب (g)" : "Shot weight (g)") : (isRTL ? "الماء (g)" : "Water (g)")}
              </Label>
              <Input type="number" step="0.1" min="1" value={yieldG} onChange={(e) => setYieldG(e.target.value)} className="mt-2 h-11 rounded-xl border-[#2A2421]/15" data-testid="ext-yield-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {mode === "pro" && (
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#5C544F]">TDS %</Label>
                <Input type="number" step="0.01" min="0.1" value={tds} onChange={(e) => setTds(e.target.value)} className="mt-2 h-11 rounded-xl border-[#2A2421]/15" data-testid="ext-tds-input" />
              </div>
            )}
            <div className={mode === "pro" ? "" : "col-span-2"}>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#5C544F]">
                {isRTL ? "الزمن (ثانية)" : "Time (sec)"}
                <span className="ms-1 opacity-60">{isRTL ? "(اختياري)" : "(optional)"}</span>
              </Label>
              <Input type="number" min="0" value={timeS} onChange={(e) => setTimeS(e.target.value)} className="mt-2 h-11 rounded-xl border-[#2A2421]/15" data-testid="ext-time-input" />
            </div>
          </div>

          <Button onClick={onCalc} disabled={busy} className="w-full h-11 rounded-xl bg-[#2A2421] hover:bg-[#2A2421]/90 text-[#F9F6F0]" data-testid="ext-calculate-btn">
            {busy ? <Loader2 className="w-4 h-4 me-2 animate-spin" /> : <TrendingUp className="w-4 h-4 me-2" />}
            {isRTL ? "احسب الاستخلاص" : "Calculate extraction"}
          </Button>

          <div className="text-[10px] text-[#5C544F] pt-2 border-t border-[#2A2421]/10">
            <strong>SCA:</strong> {isRTL
              ? `الاستخلاص المثالي ${idealEy[0]}–${idealEy[1]}% · TDS ${idealTds[0]}–${idealTds[1]}%`
              : `Ideal EY ${idealEy[0]}–${idealEy[1]}% · TDS ${idealTds[0]}–${idealTds[1]}%`}
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="lg:col-span-7 space-y-6">
        {result && (
          <>
            <div className="soft-card rounded-2xl p-6 lg:p-8" data-testid="ext-result">
              <div className="flex items-baseline justify-between gap-4 mb-6 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-[#4A5D4E] mb-2">{isRTL ? "النتيجة" : "Result"}</div>
                  <h3 className={`${serifClass} text-2xl text-[#2A2421]`}>
                    {isRTL ? "نسبة الاستخلاص" : "Extraction Yield"}
                  </h3>
                </div>
                <div className="text-end">
                  <div className="text-xs uppercase tracking-[0.25em] text-[#5C544F]">EY</div>
                  <div className={`${serifClass} text-5xl text-[#C86240]`} data-testid="ext-ey-value">{result.ey_pct}%</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-6">
                <span className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-xs ${labelColor(result.extraction_label)}`} data-testid="ext-extraction-label">
                  {result.extraction_label === "ideal" ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                  <span><strong>{isRTL ? "الاستخلاص:" : "Extraction:"}</strong> {(isRTL ? LABELS_AR : LABELS_EN)[result.extraction_label]}</span>
                </span>
                <span className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-xs ${labelColor(result.strength_label)}`} data-testid="ext-strength-label">
                  <Flame className="w-4 h-4" />
                  <span><strong>{isRTL ? "القوة:" : "Strength:"}</strong> {(isRTL ? LABELS_AR : LABELS_EN)[result.strength_label]}</span>
                </span>
              </div>

              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-xl bg-[#F9F6F0] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#5C544F]"><Droplet className="w-3 h-3 inline me-1" /> TDS</div>
                  <div className="text-xl font-semibold text-[#2A2421] mt-1">{result.tds_pct}%</div>
                  {result.estimated && <div className="text-[9px] text-amber-700 mt-0.5">{isRTL ? "تقديري" : "estimated"}</div>}
                </div>
                <div className="rounded-xl bg-[#F9F6F0] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#5C544F]">{isRTL ? "النسبة" : "Ratio"}</div>
                  <div className="text-xl font-semibold text-[#2A2421] mt-1">1:{result.ratio}</div>
                </div>
                <div className="rounded-xl bg-[#F9F6F0] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#5C544F]">{isRTL ? "العائد" : "Yield"}</div>
                  <div className="text-xl font-semibold text-[#2A2421] mt-1">{result.yield_g}g</div>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                <Button onClick={onGetAdvice} disabled={adviceBusy} variant="outline" className="rounded-full border-[#C86240]/40 text-[#C86240] hover:bg-[#C86240]/10" data-testid="ext-ai-advice-btn">
                  {adviceBusy ? <Loader2 className="w-4 h-4 me-2 animate-spin" /> : <Sparkles className="w-4 h-4 me-2" />}
                  {isRTL ? "توصية AI" : "AI advice"}
                </Button>
                {eligibleForChallenge && submitted === null && (
                  <Button onClick={onSubmitChallenge} className="rounded-full bg-gradient-to-r from-[#C86240] to-[#a04f33] text-white hover:opacity-90" data-testid="ext-challenge-submit-btn">
                    <Trophy className="w-4 h-4 me-2" />
                    {isRTL ? "أرسل لتحدي الأسبوع" : "Submit to challenge"}
                  </Button>
                )}
                {submitted !== null && submitted > 0 && (
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm" data-testid="ext-challenge-submitted-pill">
                    <Trophy className="w-4 h-4" />
                    {isRTL ? `تم تسجيلك في تحدّي الأسبوع · +${submitted} نقطة` : `Locked into weekly challenge · +${submitted} pts`}
                  </span>
                )}
                {submitted === 0 && (
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="ext-challenge-locked-pill">
                    <Trophy className="w-4 h-4" />
                    {isRTL ? "سجّلت محاولتك اليوم — يعود غداً" : "Already submitted today — try tomorrow"}
                  </span>
                )}
                {user && (
                  <>
                    <Input
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder={isRTL ? "ملاحظات (اختياري)" : "Notes (optional)"}
                      className="flex-1 min-w-[160px] h-10 rounded-full text-xs"
                      data-testid="ext-notes-input"
                    />
                    <Button onClick={onSave} className="rounded-full bg-[#2A2421] hover:bg-[#2A2421]/90 text-[#F9F6F0]" data-testid="ext-save-log-btn">
                      <Save className="w-4 h-4 me-2" />
                      {isRTL ? "احفظ" : "Save"}
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* AI advice card */}
            {advice && (advice.advice_ar || advice.advice_en) && (
              <div className="soft-card rounded-2xl p-6 border-l-4 border-[#C86240]" data-testid="ext-advice-card">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-[#C86240]" />
                  <div className="text-xs uppercase tracking-[0.25em] text-[#4A5D4E]">{isRTL ? "توصية الباريستا الذكي" : "AI Barista coach"}</div>
                </div>
                <p className="text-sm text-[#2A2421] leading-relaxed mb-3">
                  {lang === "ar" ? advice.advice_ar : advice.advice_en}
                </p>
                {advice.action_steps?.length > 0 && (
                  <ul className="space-y-1.5">
                    {advice.action_steps.map((s, i) => (
                      <li key={i} className="text-xs text-[#5C544F] flex gap-2">
                        <span className="text-[#C86240] font-bold">→</span> {s}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* SCA Brewing Control Chart */}
            <div className="soft-card rounded-2xl p-6" data-testid="ext-sca-chart">
              <div className="text-xs uppercase tracking-[0.25em] text-[#4A5D4E] mb-3">{isRTL ? "خريطة SCA (TDS × EY)" : "SCA Brewing Control Chart"}</div>
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <ScatterChart margin={{ top: 16, right: 16, bottom: 32, left: 8 }}>
                    <CartesianGrid stroke="#e8e2d8" />
                    <XAxis type="number" dataKey="x" domain={xDomain} tick={{ fontSize: 11, fill: "#5C544F" }} label={{ value: "EY %", position: "insideBottom", offset: -16, fontSize: 11, fill: "#5C544F" }} />
                    <YAxis type="number" dataKey="y" domain={yDomain} tick={{ fontSize: 11, fill: "#5C544F" }} label={{ value: "TDS %", angle: -90, position: "insideLeft", fontSize: 11, fill: "#5C544F" }} />
                    <ReferenceArea x1={idealEy[0]} x2={idealEy[1]} y1={idealTds[0]} y2={idealTds[1]} fill="#4A5D4E" fillOpacity={0.18} stroke="#4A5D4E" strokeOpacity={0.4} />
                    <Tooltip cursor={{ strokeDasharray: "3 3" }} formatter={(v, n) => [v, n === "x" ? "EY%" : "TDS%"]} />
                    <Scatter data={dataPoint} fill="#C86240" shape="circle" />
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
              <p className="text-[10px] text-[#5C544F] mt-2">
                {isRTL
                  ? "المنطقة الخضراء هي المثالية بحسب SCA. النقطة البرتقالية هي تخميرتك."
                  : "The green zone is SCA's ideal target. Orange dot is your brew."}
              </p>
            </div>
          </>
        )}

        {!result && (
          <div className="soft-card rounded-2xl p-10 text-center text-[#5C544F]">
            {isRTL ? "أدخل القيم واضغط احسب الاستخلاص لتشاهد النتيجة وخريطة SCA." : "Enter values and press Calculate to see your result and the SCA chart."}
          </div>
        )}

        {user && <BrewLogList />}
      </div>
    </div>
  );
}

function BrewLogList() {
  const { isRTL, lang } = useI18n();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["brew-log"], queryFn: endpoints.brewLogList });
  const items = data?.items || [];
  if (items.length === 0) return null;

  const onDelete = async (id) => {
    try {
      await endpoints.brewLogDelete(id);
      qc.invalidateQueries({ queryKey: ["brew-log"] });
      toast.success(isRTL ? "تم الحذف" : "Deleted");
    } catch {
      toast.error(isRTL ? "تعذّر الحذف" : "Delete failed");
    }
  };

  return (
    <div className="soft-card rounded-2xl p-6" data-testid="brew-log-list">
      <div className="text-xs uppercase tracking-[0.25em] text-[#4A5D4E] mb-3">
        {isRTL ? `سجل تخميراتك (${items.length})` : `Your brew log (${items.length})`}
      </div>
      <ul className="space-y-2 max-h-72 overflow-y-auto">
        {items.map((it) => (
          <li key={it.id} className="flex items-center justify-between gap-3 bg-[#F9F6F0] rounded-xl px-3 py-2 text-xs" data-testid={`brew-log-${it.id}`}>
            <div className="flex flex-col gap-0.5 min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="uppercase tracking-widest text-[#4A5D4E]">{it.method}</span>
                <span className="text-[#2A2421]">{it.dose_g}g → {it.yield_g}g</span>
                {it.ey_pct != null && <span className="text-[#C86240] font-semibold">EY {it.ey_pct}%</span>}
                {it.extraction_label && (
                  <span className={`px-1.5 py-0.5 rounded text-[9px] border ${labelColor(it.extraction_label)}`}>
                    {(lang === "ar" ? LABELS_AR : LABELS_EN)[it.extraction_label]}
                  </span>
                )}
              </div>
              {it.notes && <div className="text-[#5C544F] truncate">{it.notes}</div>}
            </div>
            <button onClick={() => onDelete(it.id)} className="text-[#5C544F] hover:text-rose-600 shrink-0" data-testid={`brew-log-delete-${it.id}`} aria-label="Delete">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
